import { Controller } from 'stimulus'
import {
  stackedColumns, hBars, lineChart, kpiTile, renderTable, seriesColor, fmtNum, fmtDate,
  groupedColumns, multiLine, hBarsStacked, SLOTS
} from '../lib/metrics/charts'

const GROUP_NAMES = ['Group A', 'Group B', 'Group C']

// Drives the Metrics screen: collects the filter values, fetches the
// per-card JSON endpoints in parallel, and renders each card as its
// response arrives (no single blocking request).
export default class extends Controller {
  static targets = [
    'range', 'assignee', 'requester', 'template', 'groupBy',
    'filterPicker', 'filterValuePicker', 'chips',
    'kpiCreated', 'kpiCompleted', 'kpiLead', 'kpiOnTime',
    'weeklyChart', 'weeklyLegend', 'weeklyTable', 'weeklySub',
    'statusChart', 'statusTable',
    'leadChart', 'leadTable',
    'jobsPanel', 'resultsPanel', 'trendsPanel', 'technicianPanel', 'moreMenu', 'moreCount',
    'viewMenu', 'menuDefaultLabel', 'menuPinLabel',
    'compareBtn', 'groupRows', 'compareGrid', 'singleGrid',
    'cmpTiles', 'cmpLegend', 'cmpWeekly', 'cmpLead', 'cmpMix', 'cmpMixLegend', 'cmpStatusTable'
  ];

  connect() {
    this.groups = []
    this.chips = []
    this.initFromUrl()
    const url = new URLSearchParams(window.location.search)
    if (['results', 'trends', 'technician'].includes(url.get('tab'))) this.activateTab(url.get('tab'))
    else this.filterViewPills('jobs')
    this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      // only the visible tab re-renders — a hidden panel measures 0 wide and
      // the charts would fall back to a fixed 640px
      this.resizeTimer = setTimeout(() => { if (this.activeTab() === 'jobs') this.load() }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)
  }

  // Honor filters arriving via URL (saved-view links, shared URLs)
  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if ([...url.keys()].length === 0) return
    this.setSelect(this.rangeTarget, url.get('range'), '90')
    this.setSelect(this.assigneeTarget, url.get('assignee'), '')
    this.setSelect(this.requesterTarget, url.get('requester'), '')
    this.setSelect(this.templateTarget, url.get('parent_template_id'), '')
    this.setSelect(this.groupByTarget, url.get('group_by'), 'status')
    url.getAll('tags[]').forEach((value) => {
      if (value) this.chips.push({ kind: 'tag', key: '', label: 'Tag', value })
    })
    url.getAll('columns[]').forEach((raw) => {
      const [id, value] = raw.split(/:(.*)/s)
      if (!id || !value) return
      this.chips.push({ kind: 'col', key: id, label: this.chipSourceLabel(`col:${id}`, `Column #${id}`), value })
    })
    url.getAll('fields[]').forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      const key = decodeURIComponent(rawKey || '')
      if (!key || !value) return
      this.chips.push({ kind: 'field', key, label: this.chipSourceLabel(`field:${key}`, key), value })
    })
    if (this.chips.length) this.renderChips()
    const rawGroups = url.getAll('groups[]')
    if (rawGroups.length >= 2 && url.get('tab') !== 'results') {
      const options = JSON.parse(this.groupRowsTarget.dataset.columnOptions || '[]')
      this.groups = rawGroups.slice(0, 3).map((raw, slot) => ({
        slot,
        filters: raw.split('|').filter(Boolean).map((pair) => {
          const [columnId, value] = pair.split(/:(.*)/s)
          const col = options.find(o => String(o.id) === columnId)
          return { columnId, columnName: col ? col.name : `Column #${columnId}`, value }
        })
      }))
      this.enterCompare()
    }
  }

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    if (this.compareActive()) {
      this.loadCompare()
      return
    }
    const qs = this.queryString()
    this.fetchJson(`${this.data.get('kpisUrl')}?${qs}`, data => this.renderKpis(data))
    this.fetchJson(`${this.data.get('weeklyUrl')}?${qs}`, data => this.renderWeekly(data))
    this.fetchJson(`${this.data.get('statusTimeUrl')}?${qs}`, data => this.renderStatusTime(data))
    this.fetchJson(`${this.data.get('leadTrendUrl')}?${qs}`, data => this.renderLeadTrend(data))
  }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  filterParams() {
    const params = { range: this.rangeTarget.value }
    if (this.assigneeTarget.value) params.assignee = this.assigneeTarget.value
    if (this.requesterTarget.value) params.requester = this.requesterTarget.value
    if (this.templateTarget.value) params.parent_template_id = this.templateTarget.value
    return params
  }

  queryString() {
    const p = new URLSearchParams({ ...this.filterParams(), group_by: this.groupByTarget.value })
    this.appendChipParams(p)
    return p.toString()
  }

  // ---- shared chip filters: tag / job column / job field, AND-stacked
  // on top of the dropdown filters (values of one source any-of together
  // server-side) ----

  chipSourceLabel(sourceValue, fallback) {
    const opt = [...this.filterPickerTarget.options].find(o => o.value === sourceValue)
    return opt ? opt.textContent : fallback
  }

  chipValuesUrl(source) {
    if (source === 'tag') return this.data.get('tagValuesUrl')
    const [kind, key] = source.split(/:(.*)/s)
    return kind === 'col'
      ? `${this.data.get('columnValuesUrl')}?column_id=${key}`
      : `${this.data.get('fieldValuesUrl')}?${new URLSearchParams({ field: key })}`
  }

  filterChosen() {
    const source = this.filterPickerTarget.value
    const picker = this.filterValuePickerTarget
    picker.innerHTML = ''
    if (!source) {
      picker.disabled = true
      picker.appendChild(new Option('Pick a filter first', ''))
      return
    }
    picker.disabled = true
    picker.appendChild(new Option('Loading…', ''))
    this.fetchJson(this.chipValuesUrl(source), (data) => {
      picker.innerHTML = ''
      picker.appendChild(new Option('Pick a value…', ''))
      data.values.forEach(v => picker.appendChild(new Option(v, v)))
      picker.disabled = false
    }, false)
  }

  addChip() {
    const { value } = this.filterValuePickerTarget
    const source = this.filterPickerTarget.value
    if (!source || !value) return
    const [kind, key] = source === 'tag' ? ['tag', ''] : source.split(/:(.*)/s)
    const label = source === 'tag' ? 'Tag' : this.filterPickerTarget.selectedOptions[0].textContent
    const exists = this.chips.some(c => c.kind === kind && c.key === key && c.value === value)
    if (!exists) this.chips.push({ kind, key, label, value })
    this.filterPickerTarget.value = ''
    this.filterChosen()
    this.renderChips()
    this.load()
  }

  removeChip(ev) {
    this.chips.splice(Number(ev.currentTarget.dataset.index), 1)
    this.renderChips()
    this.load()
  }

  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    this.chips.forEach((f, i) => {
      const chip = document.createElement('span')
      chip.className = 'm-chip'
      const label = document.createElement('span')
      label.textContent = `${f.label}: `
      const strong = document.createElement('strong')
      strong.textContent = f.value
      label.appendChild(strong)
      chip.appendChild(label)
      const x = document.createElement('button')
      x.type = 'button'
      x.textContent = '✕'
      x.title = 'Remove filter'
      x.dataset.index = i
      x.dataset.action = 'click->metrics#removeChip'
      chip.appendChild(x)
      host.appendChild(chip)
    })
  }

  appendChipParams(p) {
    this.chips.forEach((c) => {
      if (c.kind === 'tag') p.append('tags[]', c.value)
      else if (c.kind === 'col') p.append('columns[]', `${c.key}:${c.value}`)
      else p.append('fields[]', `${encodeURIComponent(c.key)}:${c.value}`)
    })
  }

  renderKpis(data) {
    const cur = data.current
    const prev = data.previous
    const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null)
    const createdDelta = pct(cur.created, prev.created)
    kpiTile(this.kpiCreatedTarget, {
      label: 'Jobs created',
      value: fmtNum(cur.created),
      delta: createdDelta,
      deltaText: createdDelta === null ? null : `${Math.abs(createdDelta)}%`,
      spark: data.spark.created
    })
    const completedDelta = pct(cur.completed, prev.completed)
    kpiTile(this.kpiCompletedTarget, {
      label: 'Jobs completed',
      value: fmtNum(cur.completed),
      delta: completedDelta,
      deltaText: completedDelta === null ? null : `${Math.abs(completedDelta)}%`,
      spark: data.spark.completed
    })
    const leadDelta = (cur.median_lead_days !== null && prev.median_lead_days !== null)
      ? Math.round((cur.median_lead_days - prev.median_lead_days) * 10) / 10 : null
    kpiTile(this.kpiLeadTarget, {
      label: 'Median lead time',
      value: cur.median_lead_days === null ? '—' : `${fmtNum(cur.median_lead_days, 1)} d`,
      delta: leadDelta,
      deltaText: leadDelta === null ? null : `${Math.abs(leadDelta)} d`,
      upIsGood: false,
      sub: 'Submitted → complete'
    })
    const otDelta = (cur.on_time_pct !== null && prev.on_time_pct !== null)
      ? cur.on_time_pct - prev.on_time_pct : null
    kpiTile(this.kpiOnTimeTarget, {
      label: 'On-time completion',
      value: cur.on_time_pct === null ? '—' : `${cur.on_time_pct}%`,
      delta: otDelta,
      deltaText: otDelta === null ? null : `${Math.abs(otDelta)} pts`,
      sub: 'vs requested completion'
    })
  }

  renderWeekly(data) {
    const groupBy = this.groupByTarget.value
    const label = this.groupByTarget.selectedOptions[0].textContent
    this.weeklySubTarget.textContent = groupBy === 'none'
      ? 'All jobs in range · click a bar to open these jobs'
      : `Stacked by ${label.toLowerCase()} · click a bar to open these jobs`
    stackedColumns(this.weeklyChartTarget, data, {
      clickHint: 'Click to view these jobs',
      onClick: () => this.drillThrough()
    })
    this.renderLegend(data.series)
    renderTable(
      this.weeklyTableTarget,
      [{ label: 'Week' }].concat(data.series.map(s => ({ label: s.label, num: true }))),
      data.weeks.map((w, i) => [`Week of ${fmtDate(w)}`].concat(data.series.map(s => fmtNum(s.values[i] || 0)))),
    )
  }

  renderLegend(series) {
    const host = this.weeklyLegendTarget
    host.innerHTML = ''
    if (!series || series.length < 2) return
    series.forEach((s, i) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = seriesColor(s.label, i)
      key.appendChild(sw)
      key.appendChild(document.createTextNode(s.label))
      host.appendChild(key)
    })
  }

  renderStatusTime(data) {
    hBars(this.statusChartTarget, data.rows.map(r => ({
      label: r.status,
      value: r.median_days,
      tipExtra: `${fmtNum(r.jobs)} jobs`
    })), { fmt: v => `${fmtNum(v, 1)} d`, unitLabel: 'median days' })
    renderTable(
      this.statusTableTarget,
      [{ label: 'Status' }, { label: 'Median days', num: true }, { label: 'Jobs', num: true }],
      data.rows.map(r => [r.status, fmtNum(r.median_days, 1), fmtNum(r.jobs)]),
    )
  }

  renderLeadTrend(data) {
    lineChart(this.leadChartTarget, data.rows.map(r => ({
      x: r.week, y: r.median_lead_days, n: r.completed
    })), { fmt: v => `${fmtNum(v, 1)} d`, seriesLabel: 'median days', nLabel: 'jobs completed' })
    renderTable(
      this.leadTableTarget,
      [{ label: 'Week' }, { label: 'Median lead (d)', num: true }, { label: 'Jobs completed', num: true }],
      data.rows.map(r => [`Week of ${fmtDate(r.week)}`, fmtNum(r.median_lead_days, 1), fmtNum(r.completed)]),
    )
  }

  // ---- comparison groups: N sets of job-column filters layered on the
  // shared filters; the same endpoints are fetched once per group ----

  compareActive() {
    return this.groups.length >= 2
  }

  startCompare() {
    this.groups = [{ slot: 0, filters: [] }, { slot: 1, filters: [] }]
    this.enterCompare()
    this.load()
  }

  enterCompare() {
    this.renderGroupRows()
    this.singleGridTarget.style.display = 'none'
    this.compareGridTarget.style.display = 'grid'
    this.compareBtnTarget.style.display = 'none'
    // Group by only applies to the single-mode chart — in compare mode the
    // main chart is grouped by comparison group (matches the mock).
    this.groupByTarget.closest('.m-filter').style.display = 'none'
  }

  exitCompare() {
    this.groups = []
    this.groupRowsTarget.innerHTML = ''
    this.singleGridTarget.style.display = 'grid'
    this.compareGridTarget.style.display = 'none'
    this.compareBtnTarget.style.display = ''
    this.groupByTarget.closest('.m-filter').style.display = ''
    this.load()
  }

  groupSummary(group) {
    return group.filters.map(f => `${f.columnName}: ${f.value}`).join(' · ') || 'All matching jobs'
  }

  renderGroupRows() {
    const host = this.groupRowsTarget
    const options = JSON.parse(host.dataset.columnOptions || '[]')
    host.innerHTML = ''
    this.groups.forEach((group, gi) => {
      const row = document.createElement('div')
      row.className = 'm-grp-row'
      const dot = document.createElement('span')
      dot.className = 'm-gdot'
      dot.style.background = SLOTS[group.slot]
      row.appendChild(dot)
      const name = document.createElement('span')
      name.className = 'm-gname'
      name.textContent = GROUP_NAMES[group.slot]
      row.appendChild(name)

      group.filters.forEach((f, fi) => {
        const chip = document.createElement('span')
        chip.className = 'm-chip'
        const label = document.createElement('span')
        label.textContent = `${f.columnName}: `
        const strong = document.createElement('strong')
        strong.textContent = f.value
        label.appendChild(strong)
        chip.appendChild(label)
        const x = document.createElement('button')
        x.type = 'button'
        x.textContent = '✕'
        x.title = 'Remove filter'
        x.addEventListener('click', () => {
          group.filters.splice(fi, 1)
          this.renderGroupRows()
          this.load()
        })
        chip.appendChild(x)
        row.appendChild(chip)
      })

      const colSel = document.createElement('select')
      colSel.appendChild(new Option('+ Add job column…', ''))
      options.forEach(o => colSel.appendChild(new Option(o.name, o.id)))
      const valueSel = document.createElement('select')
      valueSel.appendChild(new Option('value…', ''))
      valueSel.disabled = true
      colSel.addEventListener('change', () => {
        valueSel.innerHTML = ''
        valueSel.appendChild(new Option('Loading…', ''))
        valueSel.disabled = true
        if (!colSel.value) return
        this.fetchJson(`${this.data.get('columnValuesUrl')}?column_id=${colSel.value}`, (data) => {
          valueSel.innerHTML = ''
          valueSel.appendChild(new Option('value…', ''))
          data.values.forEach(v => valueSel.appendChild(new Option(v, v)))
          valueSel.disabled = false
        })
      })
      valueSel.addEventListener('change', () => {
        if (!colSel.value || !valueSel.value) return
        group.filters.push({
          columnId: colSel.value,
          columnName: colSel.selectedOptions[0].textContent,
          value: valueSel.value
        })
        this.renderGroupRows()
        this.load()
      })
      row.appendChild(colSel)
      row.appendChild(valueSel)

      if (this.groups.length > 2) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.className = 'm-btn'
        rm.textContent = '✕ Remove group'
        rm.addEventListener('click', () => {
          this.groups.splice(gi, 1)
          this.groups.forEach((g, i) => { Object.assign(g, { slot: i }) })
          this.renderGroupRows()
          this.load()
        })
        row.appendChild(rm)
      }
      host.appendChild(row)
    })

    const actions = document.createElement('div')
    actions.className = 'm-grp-actions'
    if (this.groups.length < 3) {
      const add = document.createElement('button')
      add.type = 'button'
      add.className = 'm-btn'
      add.textContent = '+ Add comparison group'
      add.addEventListener('click', () => {
        this.groups.push({ slot: this.groups.length, filters: [] })
        this.renderGroupRows()
        this.load()
      })
      actions.appendChild(add)
    }
    const exit = document.createElement('button')
    exit.type = 'button'
    exit.className = 'm-btn'
    exit.textContent = 'Exit compare'
    exit.addEventListener('click', () => this.exitCompare())
    actions.appendChild(exit)
    host.appendChild(actions)
  }

  groupQueryString(group, extra = {}) {
    const p = new URLSearchParams({ ...this.filterParams(), ...extra })
    this.appendChipParams(p)
    group.filters.forEach(f => p.append('columns[]', `${f.columnId}:${f.value}`))
    return p.toString()
  }

  loadCompare() {
    const seq = this.loadSeq
    const fetches = this.groups.map(group => Promise.all([
      fetch(`${this.data.get('kpisUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('weeklyUrl')}?${this.groupQueryString(group, { group_by: 'status' })}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('leadTrendUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('statusTimeUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json())
    ]))
    Promise.all(fetches).then((results) => {
      if (seq !== this.loadSeq) return
      this.renderCompare(results)
    }).catch(() => {})
  }

  renderCompare(results) {
    const groups = this.groups.map((group, i) => ({
      group,
      kpis: results[i][0],
      weekly: results[i][1],
      leadTrend: results[i][2].rows,
      statusTime: results[i][3].rows,
      color: SLOTS[group.slot],
      name: GROUP_NAMES[group.slot]
    }))
    const { weeks } = groups[0].weekly

    const tiles = this.cmpTilesTarget
    tiles.innerHTML = ''
    groups.forEach((g) => {
      const tile = document.createElement('div')
      tile.className = 'm-card m-cmp-tile'
      const cur = g.kpis.current
      kpiTile(tile, {
        label: `${g.name} — median lead time`,
        dot: g.color,
        value: cur.median_lead_days === null ? '—' : `${fmtNum(cur.median_lead_days, 1)} d`,
        delta: null,
        deltaText: null,
        sub: `${this.groupSummary(g.group)} · ${fmtNum(cur.created)} jobs${cur.on_time_pct === null ? '' : ` · on-time ${cur.on_time_pct}%`}`.slice(0, 90)
      })
      tiles.appendChild(tile)
    })

    const legend = this.cmpLegendTarget
    legend.innerHTML = ''
    groups.forEach((g) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = g.color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(`${g.name}: ${this.groupSummary(g.group)}`.slice(0, 60)))
      legend.appendChild(key)
    })

    groupedColumns(this.cmpWeeklyTarget, {
      weeks,
      groups: groups.map(g => ({
        label: g.name,
        color: g.color,
        values: weeks.map((_, i) => g.weekly.series.reduce((a, s) => a + (s.values[i] || 0), 0))
      }))
    })

    multiLine(this.cmpLeadTarget, {
      weeks,
      series: groups.map(g => ({
        label: g.name,
        color: g.color,
        points: weeks.map((w) => {
          const row = g.leadTrend.find(r => r.week === w)
          return row ? row.median_lead_days : null
        })
      }))
    }, { percent: false, fmt: v => `${fmtNum(v, 1)} d` })

    const statusLabels = []
    groups.forEach(g => g.weekly.series.forEach((s) => {
      if (!statusLabels.includes(s.label)) statusLabels.push(s.label)
    }))
    const mixLegend = this.cmpMixLegendTarget
    mixLegend.innerHTML = ''
    statusLabels.forEach((label, i) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = seriesColor(label, i)
      key.appendChild(sw)
      key.appendChild(document.createTextNode(label))
      mixLegend.appendChild(key)
    })
    hBarsStacked(this.cmpMixTarget, groups.map(g => ({
      label: g.name,
      total: g.weekly.series.reduce((a, s) => a + s.values.reduce((x, y) => x + y, 0), 0),
      parts: statusLabels.map((label, i) => ({
        label,
        color: seriesColor(label, i),
        value: (g.weekly.series.find(s => s.label === label) || { values: [] }).values.reduce((x, y) => x + y, 0)
      }))
    })))

    const statuses = []
    groups.forEach(g => g.statusTime.forEach((r) => {
      if (!statuses.includes(r.status)) statuses.push(r.status)
    }))
    renderTable(
      this.cmpStatusTableTarget,
      [{ label: 'Status' }].concat(groups.map(g => ({ label: g.name, num: true }))),
      statuses.map(status => [status].concat(groups.map((g) => {
        const row = g.statusTime.find(r => r.status === status)
        return row ? `${fmtNum(row.median_days, 1)} d` : '—'
      }))),
    )
  }

  switchTab(ev) {
    this.activateTab(ev.currentTarget.dataset.tab)
  }

  activateTab(tab) {
    this.element.querySelectorAll('.m-tab-btn[data-tab]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === tab)
    })
    this.jobsPanelTarget.classList.toggle('active', tab === 'jobs')
    this.resultsPanelTarget.classList.toggle('active', tab === 'results')
    this.trendsPanelTarget.classList.toggle('active', tab === 'trends')
    // Only present when the account has the Technician Dashboard configured
    if (this.hasTechnicianPanelTarget) this.technicianPanelTarget.classList.toggle('active', tab === 'technician')
    this.filterViewPills(tab)
    // re-render the newly visible tab: its charts may hold widths measured
    // when the window was a different size (or the panel hidden)
    if (tab === 'jobs') this.load()
    if (tab === 'results') window.dispatchEvent(new CustomEvent('metrics:results-shown'))
    if (tab === 'trends') window.dispatchEvent(new CustomEvent('metrics:trends-shown'))
    if (tab === 'technician') window.dispatchEvent(new CustomEvent('metrics:technician-shown'))
  }

  // Saved views are tab-scoped: only the active tab's views show as pills
  // or drawer rows (they all share page_name metrics#show for routability).
  filterViewPills(tab) {
    this.element.querySelectorAll('[data-view-tab]').forEach((pill) => {
      const el = pill
      el.style.display = el.dataset.viewTab === tab ? '' : 'none'
    })
    const visibleDrawerRows = [...this.element.querySelectorAll('.m-more-row')]
      .filter(el => el.dataset.viewTab === tab).length
    const more = this.element.querySelector('.m-more')
    if (more) {
      more.style.display = visibleDrawerRows ? '' : 'none'
      if (this.hasMoreCountTarget) this.moreCountTarget.textContent = visibleDrawerRows
    }
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }

  drillThrough() {
    const params = new URLSearchParams(this.filterParams())
    params.delete('range') // jobs index has no created_at filter
    this.chips.forEach((c) => {
      if (c.kind === 'tag') params.append('tags[]', c.value)
      // jobs-index vocabulary for job columns (contains-match there, so a
      // superset of the metrics exact match). Job fields have no jobs-index
      // filter param — those chips can't carry over.
      else if (c.kind === 'col') params.append(`job_column_${c.key}[]`, c.value)
    })
    window.location.href = `${this.data.get('jobsUrl')}?${params.toString()}`
  }

  // ---- saved views (saved_search_params, page_name metrics#show) ----
  // Views open via their URL (params + saved_search_id); the page applies
  // URL params on load. Star = landing default, thumbtack = pinned pill.

  setSelect(selectEl, value, fallback) {
    const el = selectEl
    el.value = value || fallback
    // a saved value may reference a deleted user/template; fall back cleanly
    if (el.value !== String(value || fallback)) el.value = fallback
  }

  activeTab() {
    if (this.resultsPanelTarget.classList.contains('active')) return 'results'
    if (this.trendsPanelTarget.classList.contains('active')) return 'trends'
    if (this.hasTechnicianPanelTarget && this.technicianPanelTarget.classList.contains('active')) return 'technician'
    return 'jobs'
  }

  currentViewParams() {
    const tab = this.activeTab()
    if (tab !== 'jobs') {
      const identifier = `metrics-${tab}`
      const panels = {
        results: this.resultsPanelTarget,
        trends: this.trendsPanelTarget,
        technician: this.hasTechnicianPanelTarget ? this.technicianPanelTarget : null
      }
      const controller = panels[tab]
        && this.application.getControllerForElementAndIdentifier(panels[tab], identifier)
      if (controller) return controller.viewParams()
    }
    const params = { tab: 'jobs', ...this.filterParams(), group_by: this.groupByTarget.value }
    const tags = this.chips.filter(c => c.kind === 'tag').map(c => c.value)
    if (tags.length) params.tags = tags
    const columns = this.chips.filter(c => c.kind === 'col').map(c => `${c.key}:${c.value}`)
    if (columns.length) params.columns = columns
    const fields = this.chips.filter(c => c.kind === 'field').map(c => `${encodeURIComponent(c.key)}:${c.value}`)
    if (fields.length) params.fields = fields
    if (this.compareActive()) {
      params.groups = this.groups.map(g => g.filters.map(f => `${f.columnId}:${f.value}`).join('|'))
    }
    return params
  }

  saveView() {
    const name = window.prompt('Name this view:')
    if (!name) return
    fetch(this.data.get('savedSearchUrl'), {
      method: 'POST',
      headers: {
        'X-CSRF-Token': Rails.csrfToken(),
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        saved_search_param: { name, page_name: 'metrics#show', params: JSON.stringify(this.currentViewParams()) }
      })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  togglePin(ev) {
    this.patchView(ev.currentTarget.dataset.viewId, 'toggle_pinned')
  }

  toggleStar(ev) {
    this.patchView(ev.currentTarget.dataset.viewId, 'set_default')
  }

  // Star on the "Overview" system pill: clears the current landing view.
  clearDefault(ev) {
    const id = ev.currentTarget.dataset.defaultId
    if (id) this.patchView(id, 'set_default')
  }

  patchView(id, action) {
    fetch(`${this.data.get('savedSearchUrl')}/${id}/${action}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
    }).then(() => window.location.reload())
  }

  toggleMoreViews() {
    this.moreMenuTarget.classList.toggle('open')
  }

  // ---- per-pill kebab menu (one shared menu, jobs-index pattern) ----

  openViewMenu(ev) {
    ev.stopPropagation()
    const pill = ev.currentTarget.closest('.m-vpill')
    this.menuViewId = pill.dataset.viewId
    this.menuViewName = pill.dataset.viewName
    this.menuViewParams = pill.dataset.viewParams
    this.menuDefaultLabelTarget.textContent = pill.dataset.viewDefault === 'true'
      ? 'Remove as landing view' : 'Make this my landing view'
    this.menuPinLabelTarget.textContent = pill.dataset.viewPinned === 'true'
      ? 'Unpin from bar' : 'Pin to bar'
    const rect = ev.currentTarget.getBoundingClientRect()
    const menu = this.viewMenuTarget
    menu.style.top = `${rect.bottom + 4}px`
    menu.style.left = `${Math.max(8, rect.right - 220)}px`
    menu.classList.add('open')
    this.closeMenuHandler = () => this.closeViewMenu()
    setTimeout(() => document.addEventListener('click', this.closeMenuHandler, { once: true }), 0)
  }

  closeViewMenu() {
    this.viewMenuTarget.classList.remove('open')
  }

  menuToggleDefault(ev) {
    ev.stopPropagation()
    this.patchView(this.menuViewId, 'set_default')
  }

  menuTogglePin(ev) {
    ev.stopPropagation()
    this.patchView(this.menuViewId, 'toggle_pinned')
  }

  menuMoveLeft(ev) {
    ev.stopPropagation()
    this.moveView('left')
  }

  menuMoveRight(ev) {
    ev.stopPropagation()
    this.moveView('right')
  }

  // The shared move endpoint swaps one slot in the FULL page ordering, but
  // metrics pills are filtered by tab (hidden neighbors sit between visible
  // ones) — so step across as many slots as it takes to pass the adjacent
  // VISIBLE pill, then reload.
  moveView(direction) {
    this.closeViewMenu()
    const fullOrder = JSON.parse(this.element.querySelector('.m-views').dataset.viewOrder || '[]').map(String)
    const visibleIds = [...this.element.querySelectorAll('.m-vpill[data-view-id]')]
      .filter(pill => pill.style.display !== 'none')
      .map(pill => pill.dataset.viewId)
    const vIdx = visibleIds.indexOf(this.menuViewId)
    const neighbor = direction === 'left' ? visibleIds[vIdx - 1] : visibleIds[vIdx + 1]
    if (!neighbor) return
    const steps = Math.abs(fullOrder.indexOf(this.menuViewId) - fullOrder.indexOf(neighbor))
    if (steps < 1) return
    let chain = Promise.resolve()
    for (let i = 0; i < steps; i += 1) {
      chain = chain.then(() => fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}/move?direction=${direction}`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
      }))
    }
    chain.then(() => window.location.reload())
  }

  menuRename(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    const name = window.prompt('Rename view:', this.menuViewName)
    if (!name || name === this.menuViewName) return
    fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ saved_search_param: { name } })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  menuDuplicate(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    fetch(this.data.get('savedSearchUrl'), {
      method: 'POST',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        saved_search_param: {
          name: `${this.menuViewName} (copy)`,
          page_name: 'metrics#show',
          params: this.menuViewParams
        }
      })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  menuDelete(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    if (!window.confirm('Delete this saved view?')) return
    fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
    }).then(() => window.location.reload())
  }
}
